import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Send, Plus, MessageSquare } from "lucide-react";

const MARILYN_AVATAR = "/images/marilyn-avatar.jpeg";

export default function Chat() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) nav("/login?next=/chat");
  }, [user, loading, nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/chat/sessions").then((r) => {
      setSessions(r.data);
      if (r.data.length && !activeId) selectSession(r.data[0].id);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamText]);

  const selectSession = async (sid) => {
    setActiveId(sid);
    const { data } = await api.get(`/chat/sessions/${sid}/messages`);
    setMessages(data);
    setStreamText("");
  };

  const newSession = () => {
    setActiveId(null);
    setMessages([]);
    setStreamText("");
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", content: text };
    setMessages((m) => [...m, optimistic]);
    setStreaming(true);
    setStreamText("");

    try {
      const token = localStorage.getItem("md_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text, session_id: activeId }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let finalText = "";
      let newSid = activeId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop();
        for (const c of chunks) {
          if (c.startsWith("event: session")) {
            const m = c.match(/data: (.+)/);
            if (m) newSid = m[1].trim();
          } else if (c.startsWith("event: done")) {
            // handled by loop end
          } else if (c.startsWith("event: error")) {
            finalText += "\n\n(Marilyn had trouble reaching the studio line.)";
          } else if (c.startsWith("data: ")) {
            const t = c.slice(6).replace(/\\n/g, "\n");
            finalText += t;
            setStreamText(finalText);
          }
        }
      }

      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: finalText }]);
      setStreamText("");
      if (newSid && newSid !== activeId) {
        setActiveId(newSid);
        const { data } = await api.get("/chat/sessions");
        setSessions(data);
      } else {
        const { data } = await api.get("/chat/sessions");
        setSessions(data);
      }
    } catch (err) {
      setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "assistant", content: "I seem to have stepped away from the wheel. Try me again in a moment." }]);
    }
    setStreaming(false);
  };

  if (loading || !user) return <div className="min-h-screen bg-[#0D0C0A] pt-32 px-6 text-[#B3AFA8]">Loading…</div>;

  return (
    <main data-testid="chat-page" className="bg-[#0D0C0A] min-h-screen pt-24 pb-6 px-4 md:px-8">
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-6 h-[calc(100vh-7rem)]">
        {/* Sidebar */}
        <aside className="hidden md:block md:col-span-3 glass rounded-sm p-4 overflow-y-auto">
          <button onClick={newSession} data-testid="new-chat-btn" className="btn-gold w-full px-4 py-3 rounded-full text-xs inline-flex items-center justify-center gap-2 mb-6">
            <Plus size={14} /> New conversation
          </button>
          <p className="label-eyebrow mb-4 px-2">History</p>
          <div className="space-y-1">
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => selectSession(s.id)}
                data-testid={`session-${s.id}`}
                className={`w-full text-left px-3 py-3 rounded-sm text-sm transition-colors truncate flex items-center gap-2 ${
                  s.id === activeId ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-[#D5D0C5] hover:bg-[#1A1715]"
                }`}
              >
                <MessageSquare size={14} className="shrink-0" />
                {s.title}
              </button>
            ))}
          </div>
        </aside>

        {/* Chat panel */}
        <section className="md:col-span-9 glass rounded-sm flex flex-col overflow-hidden">
          <header className="border-b border-white/5 px-6 py-4 flex items-center gap-4">
            <img src={MARILYN_AVATAR} alt="Marilyn" className="w-11 h-11 rounded-full object-cover border border-[#D4AF37]/40" />
            <div>
              <p className="font-display text-xl text-[#F5F2EB]">Marilyn Dale</p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">At the wheel · available</p>
            </div>
          </header>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-6 py-8 space-y-6">
            {messages.length === 0 && !streamText && (
              <div className="max-w-xl mx-auto text-center py-20">
                <p className="label-eyebrow mb-5">Welcome, student</p>
                <h2 className="font-display text-3xl mb-4">Pull up a stool.</h2>
                <p className="text-[#D5D0C5] leading-relaxed">
                  Ask me anything — centering troubles, carving and texture ideas, or where to buy your first set of ribs. I'll be here.
                </p>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} className={`flex gap-4 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <img src={MARILYN_AVATAR} alt="M" className="w-9 h-9 rounded-full object-cover shrink-0 border border-[#D4AF37]/30" />
                )}
                <div className={`max-w-[75%] px-5 py-4 rounded-2xl text-[15px] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#1A1715] border border-[#332D29] text-[#F5F2EB] rounded-br-sm"
                    : "bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#F5F2EB] rounded-bl-sm"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}

            {streamText && (
              <div className="flex gap-4">
                <img src={MARILYN_AVATAR} alt="M" className="w-9 h-9 rounded-full object-cover shrink-0 border border-[#D4AF37]/30" />
                <div className="max-w-[75%] px-5 py-4 rounded-2xl rounded-bl-sm bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#F5F2EB] text-[15px] leading-relaxed whitespace-pre-wrap">
                  {streamText}<span className="animate-pulse text-[#D4AF37]">▋</span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-white/5 p-4 flex gap-3" data-testid="chat-form">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Marilyn about centering, trimming, decorating…"
              className="flex-1 bg-[#0D0C0A] border border-[#332D29] rounded-full px-5 py-3 text-[#F5F2EB] focus:outline-none focus:border-[#D4AF37] placeholder:text-[#B3AFA8]/60"
              disabled={streaming}
            />
            <button type="submit" disabled={streaming || !input.trim()} data-testid="chat-send" className="btn-gold px-6 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-50">
              <Send size={14} /> Send
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}
